import React from 'react';
import async from '../helpers/Async';
import { AxiosError } from 'axios';
import axiosLocal from '@/services/rest';
import { RouteType } from '../models/RouteType';

import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import { UserPermission } from '@/api/client';

const HomePage = async(() => import('../pages/HomePage/HomePage'));
const LoginRedirectPage = async(() => import('../layouts/LoginRedirectPage'));
const OrdersPage = async(() => import('../pages/orders/OrdersListPage'));
const AccountsPage = async(() => import('../pages/accountsPage/AccountsPage'));
const CardsTab = async(() => import('../pages/accountsPage/CardsTab'));
const IbansTab = async(() => import('../pages/accountsPage/IbansTab'));
const ClientsCreatePage = async(() => import('../pages/ClientCreatePage/ClientCreatePage'));
const ClientsPage = async(() => import('../pages/ClientsPage/./ClientsListPage'));
const ClientPage = async(() => import('../pages/ClientsPage/ClientPage'));
const ClientDetails = async(() => import('../pages/ClientsPage/ClientDetails'));
const ClientConfiguration = async(() => import('../pages/ClientsPage/ClientConfiguration'));
const ClientUsers = async(() => import('../pages/ClientsPage/ClientUsers'));
const ClientIbans = async(() => import('../pages/ClientsPage/ClientIbans'));
const ClientCardProducts = async(() => import('../pages/ClientsPage/ClientCardProducts'));
const ClientBankPaymentFees = async(() => import('../pages/ClientsPage/ClientBankPaymentFees'));
const ClientRepresentative = async(() => import('../pages/ClientsPage/ClientRepresentative'));
const OrderDetailsPage = async(() => import('../pages/orderDetails/OrderDetailsPage'));
const CreateCardPaymentInstructionPage = async(
    () => import('../pages/createPaymentInstructionPages/CreateCardPaymentInstructionPage'),
);
const CreateIbanPaymentInstructionPage = async(
    () => import('../pages/createPaymentInstructionPages/CreateBankPaymentInstructionPage'),
);
const CreateInboundPaymentPage = async(
    () => import('../pages/inboundPaymentCreatePage/InboundPaymentCreatePage'),
);
const OrderCreatePage = async(() => import('../pages/orderCreatePage/OrderCreatePage'));
const DashboardPage = async(() => import('../pages/dashboardPage/DashboardPage'));
const OrderEditPage = async(() => import('../pages/orderEditPage/OrderEditPage'));
const CardEditPage = async(() => import('../pages/cardEditPage/CardEditPage'));
const TransactionsListPage = async(() => import('../pages/transactions/TransactionsListPage'));
const DocAPIPage = async(() => import('../pages/docsApiPage/DocsAPIPage'));
const TransactionSummaryPage = async(
    () => import('../pages/transactionSummaryPage/TransactionSummaryPage'),
);
const TransactionDetailPage = async(
    () => import('../pages/transactionDetailPage/TransactionDetailPage'),
);
const OrdersListReportingPage = async(
    () => import('../pages/ordersListReportingPage/OrdersListReportingPage'),
);
const OrderReportPage = async(() => import('../pages/orderReportPage/OrderReportPage'));
const BankStatementPage = async(() => import('../pages/bankStatementPage/BankStatementPage'));
const WebhookAndApiKeysPage = async(
    () => import('../pages/webhookAndApiKeysPage/WebhookAndApiKeys'),
);
const RemittanceDetailsPage = async(() => import('../pages/remittance/RemittancePage'));
const RemittanceLinkExpiredPage = async(
    () => import('../pages/remittance/RemittanceLinkExpiredPage'),
);
const RemittanceLinkNotActivePage = async(
    () => import('../pages/remittance/RemittanceLinkNotActivePage'),
);
const CardCreatePage = async(() => import('../pages/cardCreatePage/CardCreatePage'));
const CardsPage = async(() => import('../pages/cardsPage/cardsPage'));
const UsersPage = async(() => import('../pages/usersPage/usersPage'));
const AccountSettingPage = async(() => import('../pages/accountSettingsPage/AccountSettingsPage'));

type MenuIconProps = {
    iconName: string;
};
const MenuIcon: React.FC<MenuIconProps> = ({ iconName }) => {
    return <span className="material-symbols-sharp icon">{iconName}</span>;
};

const ERROR_500_PATH = '/error-500';

/**
 * A request blocked by CORS (or failing at the network layer for any other reason) never
 * produces a response, so `error.response` is undefined and axios reports `ERR_NETWORK`.
 * The backend is just as unreachable as on a 5xx, so both land on the same error page.
 */
const isBackendUnreachable = (error: any): boolean =>
    error?.response ? error.response.status >= 500 : error?.code === AxiosError.ERR_NETWORK;

axiosLocal.interceptors.response.use(
    (r) => r,
    (error) => {
        if (isBackendUnreachable(error) && window.location.pathname !== ERROR_500_PATH) {
            window.location.href = ERROR_500_PATH;
        }
        return Promise.reject(error);
    },
);

export const ordersRoutes: RouteType = {
    id: 'Orders',
    path: '/orders',
    icon: <MenuIcon iconName="content_paste" />,
    component: OrdersPage,
    allowedPermissions: [UserPermission.OrdersManager, UserPermission.OrdersViewer],
    VACRequiredPermissions: [UserPermission.ViewClientOrders],
};

export const orderDetailPageRoutes: RouteType = {
    id: 'orderDetails',
    path: '/orders/:orderReference',
    component: OrderDetailsPage,
    allowedPermissions: [UserPermission.OrdersManager, UserPermission.OrdersViewer],
    VACRequiredPermissions: [UserPermission.ViewClientOrders],
};

export const cardPaymentInstructionRoutes: RouteType = {
    id: 'cardPaymentInstruction',
    path: '/orders/:orderReference/create-card-payment-instruction',
    component: CreateCardPaymentInstructionPage,
    allowedPermissions: [UserPermission.CardPaymentManager],
    VACRequiredPermissions: [UserPermission.ManageClientPayments],
};

export const editCardPaymentInstructionRoutes: RouteType = {
    id: 'editCardPaymentInstruction',
    path: '/orders/:orderReference/:paymentId/edit-card-payment',
    component: CreateCardPaymentInstructionPage,
    allowedPermissions: [UserPermission.CardPaymentManager],
    VACRequiredPermissions: [UserPermission.ManageClientPayments],
};

export const editBankPaymentInstructionRoutes: RouteType = {
    id: 'editBankPaymentInstruction',
    path: '/orders/:orderReference/:paymentId/edit-bank-payment',
    component: CreateIbanPaymentInstructionPage,
    allowedPermissions: [UserPermission.BankPaymentManager],
    VACRequiredPermissions: [UserPermission.ManageClientPayments],
};
export const ibanPaymentInstructionRoutes: RouteType = {
    id: 'ibanPaymentInstruction',
    path: '/orders/:orderReference/create-iban-payment-instruction',
    component: CreateIbanPaymentInstructionPage,
    allowedPermissions: [UserPermission.BankPaymentManager],
    VACRequiredPermissions: [UserPermission.ManageClientPayments],
};

export const inboundPaymentRoutes: RouteType = {
    id: 'defaultInboundPayment',
    path: '/orders/:orderReference/create-inbound-payment',
    component: CreateInboundPaymentPage,
    allowedPermissions: [UserPermission.PaymentManager],
    VACRequiredPermissions: [UserPermission.ManageClientPayments],
};
export const updateInboundPaymentRoutes: RouteType = {
    id: 'defaultInboundPayment',
    path: '/orders/:orderReference/:paymentId/update',
    component: CreateInboundPaymentPage,
    allowedPermissions: [UserPermission.PaymentManager],
    VACRequiredPermissions: [UserPermission.ManageClientPayments],
};

export const paymentInstructionWithCardWithoutRoutes: RouteType = {
    id: 'paymentInstructionWithPreselectedCard',
    path: '/cards/:cardId/create-card-payment-instruction',
    component: CreateCardPaymentInstructionPage,
    allowedPermissions: [UserPermission.CardPaymentManager],
    VACRequiredPermissions: [UserPermission.ManageClientPayments],
};

export const orderEditPageRoutes: RouteType = {
    id: 'orderEditPage',
    path: '/orders/:orderReference/edit',
    component: OrderEditPage,
    allowedPermissions: [UserPermission.OrdersManager],
    VACRequiredPermissions: [UserPermission.ManageClientOrders],
};

export const cardEditPageRoutes: RouteType = {
    id: 'cardEditPage',
    path: '/cards/:cardId/edit',
    component: CardEditPage,
    allowedPermissions: [UserPermission.CardManager],
    VACRequiredPermissions: [UserPermission.ManageClientCards],
};

export const orderCreatePageRoutes: RouteType = {
    id: 'orderCreatePage',
    path: '/orders/create',
    component: OrderCreatePage,
    allowedPermissions: [UserPermission.OrdersManager],
    VACRequiredPermissions: [UserPermission.ManageClientOrders],
};

export const dashboardPageRoutes: RouteType = {
    id: 'dashboardPage',
    path: '/dashboard',
    component: DashboardPage,
    necessaryPermissions: [UserPermission.IbanViewer, UserPermission.ReportingManager],
    allowedPermissions: [UserPermission.IbanViewer, UserPermission.ReportingManager],
};

export const transactionsPageRoutes: RouteType = {
    id: 'Transactions',
    path: '/transactions',
    component: TransactionsListPage,
    icon: <MenuIcon iconName="list_alt" />,
    allowedPermissions: [UserPermission.TransactionsManager],
    VACRequiredPermissions: [UserPermission.ViewClientTransactions],
};

export const docsAPIPageRoutes: RouteType = {
    id: 'Documentation',
    path: '/documentation',
    icon: <DocumentScannerOutlinedIcon />,
    component: DocAPIPage,
    VACRequiredPermissions: [],
};

export const accountSettingsRoutes: RouteType = {
    id: 'Account Settings',
    path: '/account-settings',
    icon: <DocumentScannerOutlinedIcon />,
    component: AccountSettingPage,
};

export const clientsCreateRoutes: RouteType = {
    id: 'ClientsCreate',
    component: ClientsCreatePage,
    allowedPermissions: [UserPermission.ManageClients],
    path: '/clients/create',
};

export const clientsEditRoutes: RouteType = {
    id: 'ClientsEdit',
    component: ClientsCreatePage,
    allowedPermissions: [UserPermission.ManageClients],
    path: '/clients/:clientId/edit',
};

export const clientsRoutes: RouteType = {
    id: 'Clients',
    path: '/clients',
    icon: <MenuIcon iconName="account_box" />,
    component: ClientsPage,
    allowedPermissions: [UserPermission.ViewClients],
};

export const clientDetailsPageRoutes: RouteType = {
    id: 'clientsDetailsPage',
    path: '/clients/:clientId',
    component: ClientPage,
    children: null,
    nestedChildren: [
        {
            path: 'details',
            name: 'Details',
            component: ClientDetails,
        },
        {
            path: 'representative',
            name: 'Representative',
            component: ClientRepresentative,
        },
        {
            path: 'configuration',
            name: 'Configuration',
            component: ClientConfiguration,
        },
        {
            path: 'users',
            name: 'Users',
            component: ClientUsers,
        },
        {
            path: 'ibans',
            name: 'IBANS',
            component: ClientIbans,
        },
        {
            path: 'card-products',
            name: 'Card Products',
            component: ClientCardProducts,
        },
        {
            path: 'bank-fees',
            name: 'Bank payment fees',
            component: ClientBankPaymentFees,
        },
    ],
};

export const reportsPageRoutes: RouteType = {
    id: 'Reporting',
    path: '/reporting',
    icon: <MenuIcon iconName="analytics" />,
    component: null,
    allowedPermissions: [UserPermission.ReportingManager],
    VACRequiredPermissions: [UserPermission.ViewClientReports],
    children: [
        {
            name: 'Transaction summary',
            path: '/reporting/transaction-summary',
            component: TransactionSummaryPage,
            allowedPermissions: [UserPermission.ReportingManager],
            VACRequiredPermissions: [UserPermission.ViewClientReports],
        },
        {
            name: 'Transaction detail',
            path: '/reporting/transaction-detail',
            component: TransactionDetailPage,
            allowedPermissions: [UserPermission.ReportingManager],
            VACRequiredPermissions: [UserPermission.ViewClientReports],
        },
        {
            name: 'Order detail',
            path: '/reporting/orders',
            component: OrdersListReportingPage,
            allowedPermissions: [UserPermission.ReportingManager],
            VACRequiredPermissions: [UserPermission.ViewClientReports],
        },
        {
            name: 'Bank statement',
            path: '/reporting/bank-statement',
            component: BankStatementPage,
            allowedPermissions: [UserPermission.ReportingManager],
            VACRequiredPermissions: [UserPermission.ViewClientReports],
        },
    ],
};

const OrderReportPageRoutes: RouteType = {
    id: 'OrderReportPage',
    path: '/reporting/orders/:orderReference',
    icon: <DocumentScannerOutlinedIcon />,
    component: OrderReportPage,
    allowedPermissions: [UserPermission.ReportingManager, UserPermission.OrdersManager],
    VACRequiredPermissions: [UserPermission.ManageClientOrders],
};

export const webhookAndApiKeysPageRoutes: RouteType = {
    id: 'WebhookAndApiKeys',
    path: '/webhookAndApiKeys',
    icon: <DocumentScannerOutlinedIcon />,
    component: WebhookAndApiKeysPage,
    allowedPermissions: [UserPermission.Developer],
    VACRequiredPermissions: [UserPermission.ViewClientIntegration],
};

export const accountsPageRoutes: RouteType = {
    id: 'Accounts',
    path: '/accounts',
    icon: <MenuIcon iconName="account_balance" />,
    component: AccountsPage,
    allowedPermissions: [UserPermission.IbanViewer],
    VACRequiredPermissions: [UserPermission.ViewClientIbans],
};

export const cardsPageRoutes: RouteType = {
    id: 'Cards',
    path: '/cards',
    icon: <MenuIcon iconName="credit_card" />,
    component: CardsPage,
    allowedPermissions: [UserPermission.CardManager, UserPermission.CardViewer],
    VACRequiredPermissions: [UserPermission.ViewClientCards],
};

export const usersPageRoutes: RouteType = {
    id: 'Users',
    path: '/users',
    icon: <MenuIcon iconName="person" />,
    component: UsersPage,
    allowedPermissions: [UserPermission.UserManager],
    VACRequiredPermissions: [UserPermission.ViewClientUsers],
};

export const remittanceDetailsPageRoutes: RouteType = {
    id: 'remittanceDetailsPage',
    path: '/remittance/:token',
    component: RemittanceDetailsPage,
};

export const remittanceLinkExpiredPageRoutes: RouteType = {
    id: 'remittanceLinkExpiredPage',
    path: '/remittance/link-expired',
    component: RemittanceLinkExpiredPage,
};

export const remittanceLinkNotActivePageRoutes: RouteType = {
    id: 'remittanceLinkNotActivePage',
    path: '/remittance/link-not-active',
    component: RemittanceLinkNotActivePage,
};

export const cardCreatePageRoutes: RouteType = {
    id: 'cardCreatePage',
    path: '/cards/create',
    component: CardCreatePage,
    allowedPermissions: [UserPermission.CardManager],
    VACRequiredPermissions: [UserPermission.ManageClientCards],
};

export const homePageRoutes: RouteType = {
    id: 'homePage',
    path: '/',
    component: HomePage,
};

const loginPageRoutes: RouteType = {
    id: 'loginPage',
    path: '/login',
    children: null,
    component: LoginRedirectPage,
};

export const routes = [
    ordersRoutes,
    orderDetailPageRoutes,
    cardPaymentInstructionRoutes,
    editCardPaymentInstructionRoutes,
    editBankPaymentInstructionRoutes,
    ibanPaymentInstructionRoutes,
    paymentInstructionWithCardWithoutRoutes,
    transactionsPageRoutes,
    orderEditPageRoutes,
    cardEditPageRoutes,
    orderCreatePageRoutes,
    accountSettingsRoutes,
    reportsPageRoutes,
    clientsRoutes,
    clientDetailsPageRoutes,
    webhookAndApiKeysPageRoutes,
    accountsPageRoutes,
    cardCreatePageRoutes,
    cardsPageRoutes,
    usersPageRoutes,
    inboundPaymentRoutes,
    updateInboundPaymentRoutes,
    dashboardPageRoutes,
    OrderReportPageRoutes,
    clientsCreateRoutes,
    clientsEditRoutes,
];

export const headerRoutes = [
    clientsRoutes,
    ordersRoutes,
    cardsPageRoutes,
    transactionsPageRoutes,
    accountsPageRoutes,
    reportsPageRoutes,
];

export const publicRoutes = [
    homePageRoutes,
    docsAPIPageRoutes,
    remittanceDetailsPageRoutes,
    remittanceLinkExpiredPageRoutes,
    remittanceLinkNotActivePageRoutes,
    loginPageRoutes,
];
